import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface BgRecentVisit {
  id: string;
  name: string;
  path: string;
  timestamp: number;
  type: 'model' | 'comparison';
  icon: string;
}

@Injectable({
  providedIn: 'root'
})
export class BgRecentVisitsService {
  private readonly STORAGE_KEY = 'bg_recent_visits';
  private readonly MAX_VISITS = 3;

  private recentVisitsSubject = new BehaviorSubject<BgRecentVisit[]>([]);
  public recentVisits$: Observable<BgRecentVisit[]> = this.recentVisitsSubject.asObservable();

  constructor() {
    this.loadFromStorage();
  }

  addVisit(visit: Omit<BgRecentVisit, 'timestamp'>): void {
    const newVisit: BgRecentVisit = {
      ...visit,
      timestamp: Date.now()
    };

    // Get current visits and remove any existing entry with the same ID
    const currentVisits = this.recentVisitsSubject.value;
    const filteredVisits = currentVisits.filter(v => v.id !== newVisit.id);

    // Add new visit at the beginning and limit to MAX_VISITS
    const updatedVisits = [newVisit, ...filteredVisits].slice(0, this.MAX_VISITS);

    // Update the subject and save to storage
    this.recentVisitsSubject.next(updatedVisits);
    this.saveToStorage(updatedVisits);
  }

  clearVisits(): void {
    this.recentVisitsSubject.next([]);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  private loadFromStorage(): void {
    const storedVisits = localStorage.getItem(this.STORAGE_KEY);
    if (storedVisits) {
      try {
        const visits = JSON.parse(storedVisits) as BgRecentVisit[];
        this.recentVisitsSubject.next(visits);
      } catch (error) {
        console.error('Error parsing recent visits from storage:', error);
        this.recentVisitsSubject.next([]);
      }
    }
  }

  private saveToStorage(visits: BgRecentVisit[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visits));
  }
}
